// Copyright 2026 Granite (granite.md). All rights reserved.
// Proprietary and confidential. Unauthorized copying is prohibited.

/**
 * The sidebar's width, set by dragging its right edge (or with the arrow keys on it) between
 * 200 and 400 pixels; a double click puts it back. Kept per device, like a window's size.
 */
import { useState, type ReactNode } from "react";

import { t } from "../lib/i18n.js";

export const SIDEBAR_MIN = 200;
export const SIDEBAR_MAX = 400;
export const SIDEBAR_DEFAULT = 240;
const key = "granite.sidebarWidth";

export function clampSidebarWidth(px: number): number {
  return Math.round(Math.min(SIDEBAR_MAX, Math.max(SIDEBAR_MIN, px)));
}

function load(): number {
  try {
    const n = Number(localStorage.getItem(key));
    return Number.isFinite(n) && n > 0 ? clampSidebarWidth(n) : SIDEBAR_DEFAULT;
  } catch {
    return SIDEBAR_DEFAULT;
  }
}

function save(px: number): void {
  try {
    localStorage.setItem(key, String(px));
  } catch {
    // Blocked storage: the width lasts until the page reloads.
  }
}

export function useSidebarWidth(): [number, ReactNode] {
  const [width, setWidth] = useState(load);
  const set = (px: number) => {
    const next = clampSidebarWidth(px);
    setWidth(next);
    save(next);
  };
  const handle = (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={t("shell.resizeSidebar")}
      aria-valuemin={SIDEBAR_MIN}
      aria-valuemax={SIDEBAR_MAX}
      aria-valuenow={width}
      tabIndex={0}
      className="absolute inset-y-0 -right-1 z-10 hidden w-2 cursor-col-resize touch-none outline-none after:absolute after:inset-y-0 after:left-1/2 after:w-px after:-translate-x-1/2 after:bg-accent after:opacity-0 after:transition-opacity hover:after:opacity-60 focus-visible:after:opacity-100 md:block"
      onPointerDown={(e) => {
        e.preventDefault();
        const startX = e.clientX;
        const start = width;
        const el = e.currentTarget;
        el.setPointerCapture(e.pointerId);
        const move = (ev: PointerEvent) => {
          setWidth(clampSidebarWidth(start + ev.clientX - startX));
        };
        const up = (ev: PointerEvent) => {
          el.removeEventListener("pointermove", move);
          el.removeEventListener("pointerup", up);
          el.removeEventListener("pointercancel", up);
          set(start + ev.clientX - startX);
        };
        el.addEventListener("pointermove", move);
        el.addEventListener("pointerup", up);
        el.addEventListener("pointercancel", up);
      }}
      onDoubleClick={() => {
        set(SIDEBAR_DEFAULT);
      }}
      onKeyDown={(e) => {
        const step = e.shiftKey ? 40 : 16;
        if (e.key === "ArrowLeft") set(width - step);
        else if (e.key === "ArrowRight") set(width + step);
        else if (e.key === "Home") set(SIDEBAR_MIN);
        else if (e.key === "End") set(SIDEBAR_MAX);
        else return;
        e.preventDefault();
      }}
    />
  );
  return [width, handle];
}
